import React, {useEffect} from 'react';
import {useFormContext} from 'react-hook-form';
import {Form, withForm, WithFormProps, Loader} from '@innowise-group/ui-kit';
import {Office, OfficesApiService, useService} from '@innowise-group/core';
import {tap} from 'rxjs';
import OfficeFormLayout, {OfficeFormSection, OfficeFormValues} from './office-form-layout.component';
import {useEditOfficeData} from './edit-office-form.hook';

export type EditOfficeFormValues = OfficeFormValues;

type EditOfficeFormProps = WithFormProps<EditOfficeFormValues> & {
  officeId: string;
  sectionsToShow?: OfficeFormSection[];
  onCancel: () => void;
  onSave: (office: Office) => void;
  offices?: Office[];
};

const EditOfficeForm: React.FC<EditOfficeFormProps> = ({
  officeId,
  sectionsToShow = ['location', 'floor'],
  onCancel,
  onSave,
  isSubmit,
  offices,
}) => {
  const officesApi = useService(OfficesApiService);
  const {
    handleSubmit,
    reset,
    formState: {isValid, isDirty, errors, isValidating},
    trigger,
  } = useFormContext<EditOfficeFormValues>();
  const {initialValues, isLoading} = useEditOfficeData(officeId);

  useEffect(() => {
    if (initialValues) {
      reset(initialValues);
    }
  }, [initialValues, reset]);

  useEffect(() => {
    if (initialValues && isDirty && !isValidating && !isValid && Object.keys(errors).length === 0) {
      trigger();
    }
  }, [initialValues, isDirty, isValidating, isValid, errors, trigger]);

  if (isLoading) {
    return <Loader size={30} />;
  }

  return (
    <Form.Container
      onSubmit={handleSubmit((values) =>
        officesApi
          .editOffice(officeId, values)
          .pipe(
            tap((office) => {
              onSave(office);
              onCancel();
            }),
          )
          .subscribe(),
      )}
    >
      <OfficeFormLayout sectionsToShow={sectionsToShow} onCancel={onCancel} isSubmit={isSubmit} offices={offices} />
    </Form.Container>
  );
};

export default withForm(EditOfficeForm);
